/**
 * CompanyScopeSelector - Dropdown to select Global settings or Company-specific overrides
 */
import { AlertTriangle, Globe02, Building07 } from "@untitledui/icons";
import { Select } from "@/components/base/select/select";
import { useCompanies } from "@/hooks/api/use-companies";
import type { CompanyResponse } from "@/types/api/admin";

interface CompanyScopeSelectorProps {
    selectedScope: string;
    onScopeChange: (scope: string) => void;
}

export function CompanyScopeSelector({ selectedScope, onScopeChange }: CompanyScopeSelectorProps) {
    const { data: companies, isLoading } = useCompanies();

    // Build options list with standard id/label structure
    const options = [
        {
            id: "global",
            label: "Global Settings",
        },
        ...(companies || []).map((company: CompanyResponse) => ({
            id: company.company_id,
            label: company.legal_name || company.company_name || "Unknown Company",
        })),
    ];

    const selectedCompany = companies?.find((c: CompanyResponse) => c.company_id === selectedScope);

    return (
        <div className="mb-6">
            <Select
                label="Settings Scope"
                items={options}
                selectedKey={selectedScope}
                onSelectionChange={(key) => onScopeChange(key as string)}
                size="md"
                className="max-w-md"
                isDisabled={isLoading}
            >
                {(item) => (
                    <Select.Item key={item.id} id={item.id} textValue={item.label}>
                        <div className="flex items-center gap-2">
                            {item.id === "global" ? (
                                <Globe02 className="size-4 text-fg-quaternary" />
                            ) : (
                                <Building07 className="size-4 text-fg-quaternary" />
                            )}
                            <div className="flex flex-col">
                                <span className="text-primary">{item.label}</span>
                                <span className="text-xs text-tertiary">
                                    {item.id === "global" ? "Applies to all companies" : "Override for this company only"}
                                </span>
                            </div>
                        </div>
                    </Select.Item>
                )}
            </Select>

            {/* Warning banner when company is selected */}
            {selectedScope !== "global" && selectedCompany && (
                <div className="mt-4 bg-utility-warning-50 rounded-lg p-4 ring-1 ring-utility-warning-200">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="size-5 text-utility-warning-600 shrink-0" />
                        <span className="font-medium text-utility-warning-700">Company Override Mode</span>
                    </div>
                    <p className="text-sm text-utility-warning-600 mt-1 ml-7">
                        Changes here will override global settings for <strong>{selectedCompany.legal_name}</strong>{" "}
                        only. Leave fields empty to inherit from global settings.
                    </p>
                </div>
            )}
        </div>
    );
}
